import Close from '@mui/icons-material/Close';
import { buttonClasses, styled, svgIconClasses } from '@mui/material';
import Box from '@mui/material/Box';
import Button, { ButtonProps } from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import React from 'react';

import { HEADER_PANEL_Z_INDEX } from '~components/Order/ordersDispatchStyledComponents';

// The bulk-actions bar is a floating overlay that stays dark in BOTH modes, so its
// greys are deliberately mode-fixed — semantic surfaces would turn it near-white
// in light. Same reasoning applies to the data-grid bottom bar and the live-map
// back-to-live bar.
export function BulkActionsBar({ children }: React.PropsWithChildren) {
  return (
    <Box
      sx={(theme) => ({
        alignItems: 'center',
        bottom: theme.spacing(10),
        display: 'flex',
        justifyContent: 'center',
        left: 0,
        pointerEvents: 'none',
        position: 'fixed',
        right: 0,
        zIndex: HEADER_PANEL_Z_INDEX + 1,
      })}
    >
      <Box
        sx={(theme) => ({
          alignItems: 'center',
          bgcolor: theme.palette.grey[900],
          borderRadius: 1,
          boxShadow: '0px 2px 40px 0px rgba(0, 0, 0, 0.40);',
          display: 'flex',
          gap: 1,
          p: 1,
          pointerEvents: 'auto',
        })}
      >
        {children}
      </Box>
    </Box>
  );
}

export const BulkActionsBarButton = styled(Button)<ButtonProps>(({
  color = 'primary',
  theme,
}) => {
  const isInheritColor = color === 'inherit';

  return {
    backgroundColor: isInheritColor ? theme.palette.grey[800] : theme.palette[color].main,
    borderRadius: theme.spacing(0.5),
    color: isInheritColor ? theme.palette.grey[50] : theme.palette[color].contrastText,
    fontSize: '12px',
    height: '28px',
    letterSpacing: '0.01px',
    minWidth: '90px',
    [`&.${buttonClasses.root}`]: {
      fontWeight: 700,
      '&:hover': {
        backgroundColor: isInheritColor ? theme.palette.grey[600] : undefined,
      },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.grey[800],
        color: theme.palette.grey[400],
        cursor: 'not-allowed !important',
        opacity: color ? 0.8 : 1,
        pointerEvents: 'auto',
      },
      [`& .${buttonClasses.endIcon} ${svgIconClasses.root}`]: { fontSize: '16px' },
      [`& .${buttonClasses.startIcon} ${svgIconClasses.root}`]: { fontSize: '16px' },
    },
  };
});

const BulkActionsBarDashedButton = styled(Button)<ButtonProps>(({ theme }) => {
  return {
    backgroundColor: theme.palette.grey[900],
    border: `dashed 1px ${theme.palette.grey[700]}`,
    borderRadius: theme.spacing(0.5),
    color: theme.palette.grey[50],
    fontSize: '12px',
    height: '28px',
    letterSpacing: '0.01px',
    minWidth: '90px',
    [`&.${buttonClasses.root}`]: {
      fontWeight: 700,
      '&:hover': { backgroundColor: theme.palette.grey[800] },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.grey[800],
        color: theme.palette.grey[400],
        cursor: 'not-allowed !important',
        opacity: 0.8,
        pointerEvents: 'auto',
      },
      [`& .${buttonClasses.endIcon} ${svgIconClasses.root}`]: { fontSize: '16px' },
      [`& .${buttonClasses.startIcon} ${svgIconClasses.root}`]: { fontSize: '16px' },
    },
  };
});

export function BulkActionsBarCountButton(props: ButtonProps) {
  return (
    <BulkActionsBarDashedButton
      endIcon={<Close sx={(theme) => ({ color: theme.brandV2.colors.treadGray3 })} />}
      {...props}
    />
  );
}

interface BulkActionsBarTextProps {
  isStrong?: boolean;
}

export function BulkActionsBarText({
  children,
  isStrong = false,
}: React.PropsWithChildren<BulkActionsBarTextProps>) {
  return (
    <Typography
      sx={(theme) => ({
        color: theme.palette.grey[50],
        fontSize: '12px',
        fontWeight: isStrong ? 700 : 400,
      })}
    >
      {children}
    </Typography>
  );
}
